import {
    isValidElement,
    type ReactNode,
    Children
} from "react";
import type {
    FieldControlAccessibilityConstantType,
    FieldErrorMessagesConstantType,
    FieldControlAccessibilityType,
    FieldOptionListConstantType,
    FieldOptionElementProps
} from "./type";

export const getFieldControlAccessibility = ({
    isLabelled,
    context
}: FieldControlAccessibilityConstantType): FieldControlAccessibilityType => {
    if(!context) {
        return {
            isInvalid: false
        };
    }

    const describedBy = [
        context.hasDescription ? context.descriptionID : undefined,
        context.hasError ? context.errorID : undefined
    ].filter(Boolean).join(" ");

    const accessibilityHint = [
        context.hasDescription ? context.descriptionText : undefined,
        context.hasError ? context.errorText : undefined
    ].filter(Boolean).join(". ");

    const isContextLabelled = !isLabelled && context.hasTitle;

    return {
        accessibilityLabel: isContextLabelled ? context.titleText : undefined,
        labelledBy: isContextLabelled ? context.titleID : undefined,
        accessibilityHint: accessibilityHint || undefined,
        describedBy: describedBy || undefined,
        isInvalid: context.isInvalid
    };
};

export const getFieldChildrenText = (children: ReactNode): string | undefined => {
    if(typeof children === "string" || typeof children === "number") {
        return String(children);
    }

    if(!Array.isArray(children)) {
        return undefined;
    }

    const text = children.filter((child) => typeof child === "string" || typeof child === "number").join("");

    return text || undefined;
};

export const getFieldErrorMessages = ({
    children,
    errors
}: FieldErrorMessagesConstantType): string[] => {
    const childrenText = getFieldChildrenText(children);

    const messages = [
        childrenText,
        ...errors ?? []
    ].filter((message): message is string => typeof message === "string" && message.trim().length > 0);

    return Array.from(new Set(messages));
};

export const getIsFieldOptionList = ({
    isOptionGroup = false,
    children
}: FieldOptionListConstantType): boolean => {
    const items = Children.toArray(children);

    if(!items.length) {
        return false;
    }

    return items.every((item) => {
        if(!isValidElement<FieldOptionElementProps>(item) || item.props.variant === "card") {
            return false;
        }

        return isOptionGroup || "isChecked" in item.props;
    });
};
